import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PieController,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useSelector } from 'react-redux';
import LoadingSpinner from './LoadingSpinner';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PieController,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AgentDash = memo(() => {
  const [aggregatedSales, setAggregatedSales] = useState({
    dailyTotal: 0,
    weeklyTotal: 0,
    monthlyTotal: 0,
    yearlyTotal: 0,
    dailyCount: 0,
    weeklyCount: 0,
    monthlyCount: 0,
    yearlyCount: 0
  });
  const [salesByBranch, setSalesByBranch] = useState([]);
  const [salesByCashier, setSalesByCashier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useSelector((state) => state.user);
  const [branches, setBranches] = useState([]);
  const [agentStatus, setAgentStatus] = useState(null);

  // Fetch agent status
  const fetchAgentStatus = useCallback(async  () => {
    try {
      if (currentUser.role === 'agent') {
        const response = await fetch(`/api/user/branch/${currentUser._id}`);
        if (response.ok) {
          const agentData = await response.json();
          setAgentStatus(agentData.status);
        }
      }
    } catch (err) {
      console.error('Error fetching agent status:', err);
    }
  }, [currentUser._id, currentUser.role]);

  // Memoized fetch functions with error handling
  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/branch/getbranch/${currentUser.username}`);
      if (!response.ok) throw new Error('Failed to fetch branches');
      const data = await response.json();
      setBranches(data.branches || []);
      return data.branches || [];
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError("Error fetching branch data");
      throw err;
    }
  }, [currentUser.username]);

  const fetchSalesData = useCallback(async (branchNames) => {
    try {
      setLoading(true);
      setError(null);
      
      const [aggregatedResponse, groupedResponse] = await Promise.all([
        fetch(`/api/sales/salesTimeByBranch?branch=${branchNames.join(',')}`),
        fetch(`/api/sales/salesBranchByBranch?branch=${branchNames.join(',')}`)
      ]);

      if (!aggregatedResponse.ok || !groupedResponse.ok) {
        throw new Error('Failed to fetch sales data');
      }

      const [aggregatedData, groupedData] = await Promise.all([
        aggregatedResponse.json(),
        groupedResponse.json()
      ]);

      setAggregatedSales(prev => ({
        ...prev,
        ...aggregatedData
      }));
      setSalesByBranch(groupedData.salesByBranch || []);
      setSalesByCashier(groupedData.salesByCashier || []);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch sales data. Please try again later.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Combined data fetching effect
  useEffect(() => {
    const loadData = async () => {
      try {
        const branches = await fetchBranches();
        if (branches.length > 0) {
          const branchNames = branches.map(branch => branch.name);
          await fetchSalesData(branchNames);
        }
        // Fetch agent status
        await fetchAgentStatus();
      } catch (err) {
        // Errors are already handled in the fetch functions
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Optional: Set up polling for data updates
    const intervalId = setInterval(loadData, 30000); // Update every 30 seconds
    
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [fetchBranches, fetchSalesData, fetchAgentStatus]);

  // Memoized number formatter
  const formatNumber = useCallback((num) => {
    if (!num) return '0';
    if (num < 1000) return num.toLocaleString();
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    return (num / 1000).toFixed(1) + 'K';
  }, []);

  // Memoized chart data generators
  const generateChartData = useMemo(() => ({
    bar: (data, label) => ({
      labels: data.map(item => item._id),
      datasets: [{
        label,
        data: data.map(item => item.total),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(5, 38, 255, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(75, 192, 192, 0.4)',
        hoverBorderColor: 'rgba(75, 192, 192, 1)',
      }],
    }),
    pie: (data) => ({
      labels: data.map(item => item._id),
      datasets: [{
        data: data.map(item => item.total),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#FF9F40',
          '#4BC0C0',
        ],
        hoverBackgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#FF9F40',
          '#4BC0C0',
        ],
      }],
    }),
    line: (data) => ({
      labels: data.map(item => item._id),
      datasets: [{
        label: 'Sales by Branch',
        data: data.map(item => item.total),
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(5, 38, 255, 1)',
        borderWidth: 2,
        tension: 0.1,
      }],
    })
  }), []);

  // Memoized chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => `$${context.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${formatNumber(value)}`
        }
      }
    }
  }), [formatNumber]);

  // Loading state
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
        <div className="tw-text-center">
          <LoadingSpinner size="large" />
          <p className="tw-mt-4 tw-text-blue-800 tw-text-xl">Loading your dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
        <div className="tw-text-center">
          <div className="tw-text-red-500 tw-text-xl tw-mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // No branches state
  if (branches.length === 0) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
        <div className="tw-text-center tw-max-w-md tw-p-6 tw-bg-white tw-rounded-lg tw-shadow-md">
          <h3 className="tw-text-lg tw-font-semibold tw-text-blue-800 tw-mb-2">No Branches Found</h3>
          <p className="tw-text-gray-600">There are no branches assigned to your account.</p>
          <p className="tw-mt-2 tw-text-sm tw-text-gray-500">Please contact your administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen">
      {/* Agent Deactivation Warning */}
      {agentStatus === false && (
        <div className="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-6">
          <div className="tw-flex tw-items-center">
            <div className="tw-flex-shrink-0">
              <svg className="tw-h-5 tw-w-5 tw-text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="tw-ml-3">
              <h3 className="tw-text-sm tw-font-medium tw-text-red-800">
                Account Deactivated
              </h3>
              <div className="tw-mt-2 tw-text-sm tw-text-red-700">
                <p>
                  Your agent account has been deactivated by the administrator. 
                  While you can still access your dashboard, users under your branch cannot login until your account is reactivated. 
                  Please contact the administrator for assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Summary Cards */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-mb-8">
        {[
          { label: "Today's sales", value: aggregatedSales.dailyTotal, count: aggregatedSales.dailyCount },
          { label: "Weekly sales", value: aggregatedSales.weeklyTotal, count: aggregatedSales.weeklyCount },
          { label: "Monthly sales", value: aggregatedSales.monthlyTotal, count: aggregatedSales.monthlyCount },
          { label: "Yearly sales", value: aggregatedSales.yearlyTotal, count: aggregatedSales.yearlyCount }
        ].map((item, index) => (
          <div key={index} className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-flex tw-flex-col tw-items-center">
            <p className="tw-text-red-500 tw-font-bold tw-text-2xl tw-mb-1">
              ${formatNumber(item.value)}
            </p>
            <p className="tw-text-gray-600 tw-mb-2">{item.label}</p>
            {item.count > 0 && (
              <p className="tw-text-sm tw-text-gray-500">{item.count} transactions</p>
            )}
          </div>
        ))}
      </div>

      {/* Charts Section */}
      {salesByBranch.length > 0 || salesByCashier.length > 0 ? (
        <>
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-8">
            {/* Pie Chart */}
            {salesByBranch.length > 0 && (
              <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
                <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales Distribution by Branch</h2>
                <div className="tw-h-80">
                  <Pie
                    data={generateChartData.pie(salesByBranch)}
                    options={chartOptions}
                  />
                </div>
              </div>
            )}

            {/* Bar Charts */}
            <div className="tw-grid tw-grid-cols-1 tw-gap-8">
              {salesByBranch.length > 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
                  <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales by Branch</h2>
                  <div className="tw-h-80">
                    <Bar
                      data={generateChartData.bar(salesByBranch, 'Sales by Branch')}
                      options={chartOptions}
                    />
                  </div>
                </div>
              )}

              {salesByCashier.length > 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
                  <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales by Cashier</h2>
                  <div className="tw-h-80">
                    <Bar
                      data={generateChartData.bar(salesByCashier, 'Sales by Cashier')}
                      options={chartOptions}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Line Chart */}
          {salesByBranch.length > 0 && (
            <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-mb-8">
              <h2 className="tw-text-blue-800 tw-text-lg tw-font-semibold tw-mb-4">Sales Trend by Branch</h2>
              <div className="tw-h-96">
                <Line
                  data={generateChartData.line(salesByBranch)}
                  options={chartOptions}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-8 tw-text-center">
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700">No Sales Data Available</h3>
          <p className="tw-text-gray-600 tw-mt-2">There are no sales records for your branches yet.</p>
          <p className="tw-text-sm tw-text-gray-500 tw-mt-1">Data will appear here once sales are recorded</p>
        </div>
      )}
    </div>
  );
});

export default AgentDash;